import cloneDeep from 'lodash/cloneDeep'
import IOApi from '@/api/IOApi'
import { IOBudgetModel } from './IOBudget'
import { MenuModel } from '@/models/Menu'
import { CampaignRepository } from '@/models/CampaignOperation'
import { CreativeFolderRepository } from './CreativeFolder'
import PermissionUtil from './PermissionUtil'
import 'lodash'
import BudgetRequestApi from '@/api/BudgetRequest'

const isActive = status => status === 'active'

export class InsertionOrdersModel {
  constructor(item = {}) {
    const {
      id,
      anIoId,
      name,
      code,
      advertiserId,
      advertiserName,
      ownerProfileId,
      permission,
      status,
      agencyMarginId,
      note,
      billingPeriods,
      salesAccountId,
      salesAccountName,
      isCreatedOnAN,
    } = item
    this.id = id || undefined
    this.anIoId = Number(anIoId) || undefined
    this.name = name || ''
    this.code = code || ''
    this.advertiserId = Number(advertiserId) || undefined
    this.advertiserName = advertiserName || undefined
    this.ownerProfileId = Number(ownerProfileId) || undefined
    this.permission = permission || undefined
    this.status = status || undefined
    this.agencyMarginId = Number(agencyMarginId) || undefined
    this.note = note || ''
    this.billingPeriods = billingPeriods || undefined
    this.salesAccountId = Number(salesAccountId) || undefined
    this.salesAccountName = salesAccountName || undefined
    this.isCreatedOnAN = isCreatedOnAN
    // 以下、パラメータを受け取らないプロパティ
    this.budgetList = []
    this.salesAccountList = []
    this.menuList = []
    this.campaigns = []
    this.creativeFolder = null
  }

  static BUDGET_LIMIT = 52

  get isActive() {
    return isActive(this.status)
  }

  clone() {
    return cloneDeep(this)
  }

  /**
   * 新規案件かどうか
   * @return {Boolean}
   */
  get isNew() {
    return typeof this.id === 'undefined'
  }

  get isEditable() {
    return (
      ['operator', 'admin'].includes(this.permission) && !this.isCreatedOnAN
    )
  }
  get canEditCampaign() {
    return this.isEditable
  }

  get isOverBudgetLimit() {
    return (
      this.filteredValidBudgetListLength > InsertionOrdersModel.BUDGET_LIMIT
    )
  }

  get filteredValidBudgetListLength() {
    return this.budgetList.filter(
      budget =>
        !(
          budget.budget === 0 &&
          typeof budget.startTime === 'undefined' &&
          typeof budget.endTime === 'undefined' &&
          typeof budget.id === 'undefined' &&
          budget.latestRequest.requestStatus === 'rejected'
        ),
    ).length
  }

  isHigherReadonly() {
    return PermissionUtil.isHigherReadonly(this.permission)
  }

  /**
   * 案件予算リスト
   * @return {IOBudgetModel[]}
   */
  async getBudgetList() {
    const result = await IOApi.fetchIOBudgetList({ IOId: this.id })
    return result.items.map(budget => new IOBudgetModel(budget))
  }

  /**
   * 案件予算リストを取得する。
   */
  async fetchBudgetList() {
    this.budgetList = await this.getBudgetList()
  }

  /**
   * 指定した案件予算を1件取得
   * @return {IOBudgetModel}
   */
  async getBudget(budgetId) {
    const list = await this.getBudgetList()
    return list.find(budget => budget.id === budgetId)
    // TODO: API が実装されたらコメントを外す
    // const data = await IOApi.fetchIOBudget({
    //   insertionOrderId: this.id,
    //   id: budgetId,
    // })
    // return new IOBudgetModel(data)
  }

  /**
   * 案件予算を追加する。
   * @param {BudgetModel} budget
   */
  addBudget(budget) {
    this.budgetList = this.budgetList.concat(budget)
  }

  /**
   * 案件予算を削除する。
   */
  async deleteBudget(budgetId) {
    await IOApi.deleteIOBudget({
      insertionOrderId: this.id,
      insertionOrderBudgetId: budgetId,
    })
  }

  async getMenuList() {
    const result = await IOApi.fetchMenuList(this.id, {
      isIncludeDeleted: false,
      page_size: 200,
    })
    return result.items.map(menu => new MenuModel(menu))
  }

  async fetchMenuList() {
    this.menuList = await this.getMenuList()
  }

  async getCampaignList() {
    const { id, advertiserId } = this
    const campaignStatuses = ['active', 'inactive']
    const ret = await CampaignRepository.search({
      advertiserId: advertiserId,
      insertionOrder: {
        ids: [id],
      },
      includeDeleted: false,
      campaignStatuses: campaignStatuses,
      pageNo: 1,
      pageSize: 20,
    })
    return ret.items
  }

  async fetchCampaignList() {
    const campaigns = await this.getCampaignList()
    this.campaigns = campaigns
  }

  async getCreativeFolders() {
    return await CreativeFolderRepository.getRootByOwnerProfileId(
      this.ownerProfileId,
    )
  }

  /**
   * クリエイティブフォルダーを検索し、ルートフォルダをセットする。
   */
  async fetchCreativeFolders() {
    this.creativeFolder = await this.getCreativeFolders()
  }

  /**
   * 保持しているクリエイティブフォルダから指定されたIDのものを検索し、返す。
   */
  findCreativeFolder(creativeFolderId) {
    if (this.creativeFolder == null) return null
    return this.creativeFolder.findCreativeFolder(creativeFolderId)
  }

  /**
   * 案件を削除
   */
  async destroy() {
    if (this.isNew) throw new Error('Cannot destroy new IO')
    try {
      await IOApi.deleteIO(this)
    } catch (e) {
      console.error(e)
      throw new Error('Cannot delete IO')
    }
  }

  static async search(params) {
    try {
      const result = await IOApi.fetchIOList({
        keyword: params.keyword,
        advertiserId: params.advertiserId,
        isIncludeDeleted: params.isIncludeDeleted,
        period: params.period,
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        withoutUnrequestedBudget: params.withoutUnrequestedBudget,
        ioStatuses: params.ioStatuses,
        sortKey: params.sortKey,
        sortDirection: params.sortDirection,
      })
      return result
    } catch (e) {
      console.error(e)
      return []
    }
  }
}

export class AbstractInsertionOrdersForm {}

export class CreateInsertionOrdersForm extends AbstractInsertionOrdersForm {
  constructor({
    name,
    code,
    advertiserId,
    agencyMarginId,
    note,
    salesAccountId,
    budgetList,
  }) {
    super()
    this.name = name
    this.code = code
    this.advertiserId = advertiserId
    this.agencyMarginId = agencyMarginId
    this.note = note
    this.salesAccountId = salesAccountId
    this.budgetRequestList = budgetList || []
  }

  /**
   * 案件を作成する。
   * 同時に新規案件予算の申請、クリエイティブルートフォルダの作成も行う。
   *
   * @return {InsertionOrdersModel|undefined}
   */
  async save() {
    // フォームに書いてから全部消すと空文字になるので、undefinedに直す
    if (this.code === '') {
      this.code = undefined
    }
    if (this.note === '') {
      this.note = undefined
    }
    let insertionOrder
    try {
      insertionOrder = await this.createInsertionOrder()

      await CreativeFolderRepository.create({
        ownerProfileId: insertionOrder.ownerProfileId,
        name: insertionOrder.name,
        isRoot: true,
      })

      const insertionOrderId = insertionOrder.id
      insertionOrder.budgetRequestList = await this.createBudgetRequests(
        insertionOrderId,
      )
      return insertionOrder
    } catch (e) {
      if (insertionOrder) {
        // 案件予算申請に失敗した場合は処理を中断し案件を返す
        console.error(e)
        return insertionOrder
      } else {
        throw e
      }
    }
  }

  async createInsertionOrder() {
    try {
      const res = await IOApi.postIO(this)
      return new InsertionOrdersModel(res.item)
    } catch (e) {
      console.error(e)
      throw new Error(`案件の作成に失敗しました`)
    }
  }

  async createBudgetRequests(insertionOrderId) {
    try {
      const budgetRequestListWithIoId = {
        budgetRequests: this.budgetRequestList.map(budgetRequestListItem => ({
          insertionOrderId,
          budget: parseInt(budgetRequestListItem.budget),
          startTime: budgetRequestListItem.startTime,
          endTime: budgetRequestListItem.endTime,
          isNew: true,
        })),
      }

      const res = await BudgetRequestApi.createBulk(budgetRequestListWithIoId)
      return res
    } catch (e) {
      console.error(e)
      throw new Error('案件予算申請に失敗しました')
    }
  }

  get isValid() {
    return this.name !== '' && this.agencyMarginId != null
  }
}

export class UpdateInsertionOrdersForm extends AbstractInsertionOrdersForm {
  constructor({
    id,
    name,
    code,
    agencyMarginId,
    note,
    salesAccountId,
    status,
  }) {
    super()
    this.id = id
    this.name = name
    this.code = code
    this.agencyMarginId = agencyMarginId
    this.note = note
    this.salesAccountId = salesAccountId
    this.status = status
  }

  async save() {
    // フォームに書いてから全部消すと空文字になるので、nullに直す
    // postと違って、putはundefinedだとrequiredがパスできない
    if (this.code === '') {
      this.code = null
    }
    try {
      const res = await IOApi.putIO(this)
      return new InsertionOrdersModel(res.item)
    } catch (e) {
      console.error(e)
      throw new Error(`Error: Update insertion order ID = ${this.id}`)
    }
  }

  get isValid() {
    return this.name !== '' && this.agencyMarginId != null
  }
}
